import React from 'react';
import PropTypes from 'prop-types';

import LastUpdated from '~components/common/LastUpdated';
import StatePageAccordion, {
  AccordionGroup,
} from '~components/common/Accordion/StatePageAccordion';
import { ContentSection } from '~components/Layout';
import Markdown from '~components/common/Markdown';
import {
  canIGetAnEmergencyBallot,
  canIGetAReplacementBallot,
  canIRequestMyBallotInPerson,
  canIRequestMyBallotOnline,
  canIRequestMyBallotViaMail,
  canMyBallotBeRejected,
  doIHaveToRequestABallotEveryTime,
  howCanITrackMyMailInBallot,
  howDoesMailInVotingWork,
  howDoIReturnMyBallot,
  howDoIVoteIfMilitaryOrOverseas,
  whatAreTheVoteByMailDeadlines,
  whatIDIsRequiredToReturnABallot,
  whatIdIsRequiredToVoteByMail,
  whoCanVoteAbsentee,
} from '~utils/faqAnswersAbsentee';

function StateVoteByMailInfo({ state, info, showLastUpdated, styleTitleAsH1 }) {
  const isUniversalVbm = info.absentee_access_levels.text === 'universal';

  return (
    <ContentSection width='mid' isZebraStriped>
      {showLastUpdated && <LastUpdated array={state.state_information} />}

      <a id='vbm' aria-hidden='true' />
      <h2 className={`mt-0 ${styleTitleAsH1 && 'text-4xl md:text-5xl'}`}>
        {state.name} absentee and mail ballot guide
      </h2>

      <AccordionGroup>
        <StatePageAccordion heading='What are the deadlines for requesting and returning mail-in ballots?'>
          <Markdown
            content={whatAreTheVoteByMailDeadlines(
              info,
              state.name,
              state.code
            )}
          />
        </StatePageAccordion>
        {isUniversalVbm ? (
          <>
            <StatePageAccordion
              heading={`How does mail-in voting work in ${state.name}?`}
            >
              <Markdown content={howDoesMailInVotingWork(info, state.name)} />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`I won't be home to get my ${state.name} mail-in ballot. Can I get a replacement ballot?`}
            >
              <Markdown
                content={canIGetAReplacementBallot(
                  info,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`What ID is required to return a ballot in ${state.name}?`}
            >
              <Markdown content={whatIDIsRequiredToReturnABallot(info)} />
            </StatePageAccordion>
          </>
        ) : (
          <>
            <StatePageAccordion
              heading={`Who can vote absentee in ${state.name}?`}
            >
              <Markdown
                content={whoCanVoteAbsentee(info, state.name, state.code)}
              />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`Can I request my ballot online in ${state.name}?`}
            >
              <Markdown
                content={canIRequestMyBallotOnline(
                  info,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`Can I request my ballot via mail in ${state.name}?`}
            >
              <Markdown
                content={canIRequestMyBallotViaMail(
                  info,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`Can I request my ballot in person in ${state.name}?`}
            >
              <Markdown
                content={canIRequestMyBallotInPerson(
                  info,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
            <StatePageAccordion heading='What if I always want to vote by mail? Do I have to request a ballot every time?'>
              <Markdown
                content={doIHaveToRequestABallotEveryTime(
                  info,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`What ID is required to vote by mail in ${state.name}?`}
            >
              <Markdown content={whatIdIsRequiredToVoteByMail(info)} />
            </StatePageAccordion>
            <StatePageAccordion
              heading={`I had an emergency come up after the ${state.name} ballot request deadline. Is there any way I can still get a ballot?`}
            >
              <Markdown
                content={canIGetAnEmergencyBallot(info, state.name, state.code)}
              />
            </StatePageAccordion>
          </>
        )}
        <StatePageAccordion heading='I have a ballot. How do I return it?'>
          <Markdown
            content={howDoIReturnMyBallot(info, state.name, state.code)}
          />
        </StatePageAccordion>
        <StatePageAccordion heading='How can I track my mail-in ballot?'>
          <Markdown
            content={howCanITrackMyMailInBallot(info, state.name, state.code)}
          />
        </StatePageAccordion>
        <StatePageAccordion heading='Can my mail-in ballot be rejected? And if so, what can I do?'>
          <Markdown
            content={canMyBallotBeRejected(info, state.name, state.code)}
          />
        </StatePageAccordion>
        <StatePageAccordion heading='I am in the military or overseas. How do I vote?'>
          <Markdown
            content={howDoIVoteIfMilitaryOrOverseas(
              info,
              state.name,
              state.code
            )}
          />
        </StatePageAccordion>
      </AccordionGroup>
    </ContentSection>
  );
}

StateVoteByMailInfo.propTypes = {
  state: PropTypes.object,
  info: PropTypes.object,
  showLastUpdated: PropTypes.bool,
  styleTitleAsH1: PropTypes.bool,
};

StateVoteByMailInfo.defaultProps = {
  styleTitleAsH1: true,
};

export default StateVoteByMailInfo;
